import clsx from 'clsx';
import {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  createElement,
  ElementType,
  forwardRef,
  HTMLProps,
  JSXElementConstructor,
  MouseEventHandler,
  PropsWithChildren,
  ReactElement,
  ReactHTML
} from 'react';
import { LinkProps } from 'react-router';
import { useThemeColorPaletteAsCssTokens } from '../../hooks/use-theme-color-palette-as-css-tokens';
import { ThemeColors } from '../../theme';
import { LoadingIndicator } from '../loading-indicator';
import classes from './icon-button.module.scss';
import { Tooltip } from '../tooltip';

export type IconButtonProps = PropsWithChildren & Omit<HTMLProps<HTMLAnchorElement>, 'size'> & {
  /**
     * Custom color
     */
  color?: ThemeColors | 'inherit';

  /**
     * Custom button variant
     */
  variant?: 'filled' | 'outlined' | 'plain' | 'text';

  /**
     * Custom click handler
     */
  onClick?: MouseEventHandler;

  /**
     * Render button in disabled state
     */
  disabled?: boolean;

  /**
     * Custom element
     */
  as?: 'a' | 'button' | JSXElementConstructor<LinkProps>;

  /**
     * Customize size
     */
  size?: 'sm' | 'md' | 'lg';

  /**
     * Render as inline element like in tags or similar
     */
  inline?: boolean;

  /**
     * Custom button element type
     */
  type?: HTMLProps<HTMLButtonElement>['type'];

  /**
     * Represents the loading state of an operation or component.
     */
  isLoading?: boolean;

  /**
     * Indicates whether the specified value should be rounded.
     */
  round?: boolean;

  /**
   * Label used for the `aria-label` attribute on the icon button
   */
  label: string;

  className?: string;

  name?: string;
};


type PolymorphicProps<T extends ElementType> = IconButtonProps & {
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof IconButtonProps | 'as'>;

type PolymorphicRef<T extends ElementType> =
  ComponentPropsWithRef<T>['ref'];

type ButtonComponent = <T extends ElementType = 'button'>(
  props: PolymorphicProps<T> & {
    ref?: PolymorphicRef<T>;
  }
) => ReactElement | null;


export const IconButtonImpl = <T extends ElementType = 'button'>(
  {
    children,
    size = 'md',
    disabled,
    as,
    color = 'primary',
    variant,
    inline,
    type = 'button',
    isLoading,
    round,
    label,
    ...props
  }: PolymorphicProps<T>, ref: PolymorphicRef<T>) => {
  const style = useThemeColorPaletteAsCssTokens('--icon-button', color === 'inherit' ? 'primary' : color);

  return <Tooltip content={label}>
    {createElement(
      as as keyof ReactHTML ?? 'button',
      {
        'aria-label': label,
        ...props,
        ref,
        type,
        'tabIndex': props.tabIndex ?? (disabled ? -1 : undefined),
        'className': clsx(classes.root, props.className, classes[size], {
          [classes.disabled]: disabled,
          [classes.plain]: variant === 'plain',
          [classes.text]: variant === 'text',
          [classes.outlined]: variant === 'outlined',
          [classes.inline]: inline,
          [classes.round]: round,
          [classes.inheritColor]: color === 'inherit',
          // [classes.interactive]: !!onClick || !!props.onMouseDown,
        }),
        disabled,
        'style': disabled ? {} : style,
      },
      isLoading ? <LoadingIndicator /> : children
    )}
  </Tooltip>;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
export const IconButton = forwardRef(IconButtonImpl) as ButtonComponent;